import { Injectable } from '@angular/core';
import { cloneDeep, concat, find, get, isObject, merge, remove, set } from 'lodash';
import * as d3 from 'd3';

const showNotificationPopups = 'value.notification_popups_show';
//eslint-disable-next-line
export const ROLES_MAP: any = {
  MANAGE_PERMISSIONS: 'manageUsersPermissions',
  READ_ONLY: 'read-only',
  VIAVI_ADMIN: 'viavi-admin',
  READ_WRITE: 'read-write',
  SENSITIVE_DATA_EXPORT: 'sensitive-data-export',
  MONITOR: 'monitor',
  MANAGE_OTHER_USERS_RESULTS: 'manage-other-users-results',
  DEFAULT_ROLE: 'default_role',
  MANAGE_TESTS: 'manage-tests',
  MANAGE_SYSTEM: 'manage-system',
  NCI_CONFIG_UI: 'nci-config-ui',
  GEO_REDUNDANCY_MANAGER: 'geo-redundancy-manager'
};

// in seconds how long a user can remain idle before warning them of logout
//eslint-disable-next-line
const IDLE_TIMES: any = {
  NORMAL_USER: 5400, // 90 minutes
  MONITOR_USER: 0, // always stay active. no idle time
  DIALOG_TIMEOUT: 60, // User has 1 minute to answer
  PING_TIMEOUT: 86400 // 1 day but not used
};

//eslint-disable-next-line
const ROLE_NAME_MAP: any = {
  [ROLES_MAP.MANAGE_PERMISSIONS]: {
    friendlyName: 'Manage Internal Users',
    description: 'Ability to add users, manage their passwords, roles, groups and settings.'
  },
  [ROLES_MAP.SENSITIVE_DATA_EXPORT]: {
    friendlyName: 'Sensitive Data Export',
    description:
      'Ability to create and download reports containing corporate sensitive data such as Trace Extraction jobs (PCAP).'
  },
  [ROLES_MAP.READ_WRITE]: {
    friendlyName: 'Manage Application Settings',
    description:
      'Ability to add and modify application data such as devices, sites, and applications.'
  },
  [ROLES_MAP.MANAGE_SYSTEM]: {
    friendlyName: 'Manage System Settings',
    description: 'Ability to add and modify system data such as licenses, and logs.'
  },
  [ROLES_MAP.MANAGE_TESTS]: {
    friendlyName: 'Manage Tests',
    description: 'Ability to add, modify, and delete test related information.'
  },
  [ROLES_MAP.READ_ONLY]: {
    friendlyName: 'Read Only',
    description: 'Ability to view all data.'
  },
  [ROLES_MAP.MONITOR]: {
    friendlyName: 'Infinite Session Time',
    description:
      'Turns off the idle session timer allowing user to remain logged in until the session token expires. ' +
      "(Note, this is applicable only for the web browser session. If the user's PC goes into sleep state for an extended amount of time the session will be terminated.)"
  },
  [ROLES_MAP.MANAGE_OTHER_USERS_RESULTS]: {
    friendlyName: 'Manage User Saved Results',
    description: 'Ability to edit or delete saved jobs and reports created by other users.'
  },
  [ROLES_MAP.DEFAULT_ROLE]: {
    friendlyName: 'Standard Access',
    description: 'Ability to view all data.'
  },
  [ROLES_MAP.NCI_CONFIG_UI]: {
    friendlyName: 'External IMS Access',
    description:
      'Ability to login to the external IMS application that configures LDAP and RADIUS servers.'
  },
  [ROLES_MAP.GEO_REDUNDANCY_MANAGER]: {
    friendlyName: 'Manage Geo Redundancy',
    description: 'Ability to manage geo-redundancy settings.'
  }
};

//eslint-disable-next-line
const HIDE_ROLES: any = {
  [ROLES_MAP.READ_ONLY]: true,
  [ROLES_MAP.SENSITIVE_DATA_EXPORT]: true,
  [ROLES_MAP.MANAGE_OTHER_USERS_RESULTS]: true,
  [ROLES_MAP.VIAVI_ADMIN]: true
};

//eslint-disable-next-line
const ADD_ROLES: any = [];

@Injectable({
  providedIn: 'root'
})
export class UserAuth {
  STORAGE_KEY = 'user';
  STORAGE_KEY_USER_DATA = 'user.data';
  STORAGE_KEY_USER_PREF = 'user.pref';
  STORAGE_KEY_SETTINGS = 'settings';
  STORAGE_KEY_THEME = 'theme';
  APPLICATION = 'application_name';
  COOKIE_AUTH = 'nci-auth-token';
  EAPID = 'https://viavisolutions.com/eapid';

  //eslint-disable-next-line
  _uiSettings: any = {};
  //eslint-disable-next-line
  data: any = null;
  //eslint-disable-next-line
  pref: any = null;
  //eslint-disable-next-line
  settings: any = null;
  //eslint-disable-next-line
  token: any = null;
  //eslint-disable-next-line
  timestamp: any = new Date();

  //eslint-disable-next-line
  mos: any = [];
  //eslint-disable-next-line
  rFactor: any = [];
  //eslint-disable-next-line
  scale: any = d3.scaleLinear().domain(this.mos).range(this.rFactor);

  //eslint-disable-next-line
  userOrigin?: any;

  ldapUser: boolean;

  constructor() {
    this._init();
  }

  clear(): void {
    this.token = null;
    this.data = null;
    this.settings = null;
    this._uiSettings = {};
  }

  rFactorToMos(R: number): number {
    return (R - 60) * (100 - R) * 0.000007 * R + 0.035 * R + 1;
  }

  calculateRFactor(value: {
    valueTo: number;
    valueFrom: number;
    defaultFrom: number;
    defaultTo: number;
    maxValue: number;
  }): number {
    const getValue = item => {
      if (item < 1) {
        return item / 0.15;
      }
      return Math.round(this.scale(item));
    };
    if (isObject(value)) {
      const valuesToShow = {
        valueTo: getValue(value.valueTo),
        valueFrom: getValue(value.valueFrom),
        defaultFrom: getValue(value.defaultFrom),
        defaultTo: getValue(value.defaultTo),
        maxValue: Math.ceil(this.scale(value.maxValue))
      };

      valuesToShow.maxValue = valuesToShow.maxValue > 100 ? 100 : valuesToShow.maxValue;
      const rFactorThresholds = cloneDeep(value);
      return merge(rFactorThresholds, valuesToShow);
    }
    return getValue(value);
  }

  //eslint-disable-next-line
  getToken(): any {
    return this.token;
  }

  //eslint-disable-next-line
  get uiSettings() {
    const userSettings = get(this, 'settings', null);
    if (userSettings) {
      return find(userSettings, { key: 'UI' }) || null;
    }
    return null;
  }

  //eslint-disable-next-line
  changeUISettings(key, value) {
    const userSettings = get(this, 'settings', null);
    if (userSettings) {
      set(find(userSettings, { key: 'UI' }), key, value);
    }
  }

  isLocalUser(): boolean {
    let retval = false;

    if (this.data && this.data[this.EAPID]) {
      retval = this.data[this.EAPID] === 'local';
    }

    return retval;
  }

  canManagePermissions(): boolean {
    return this.isAuthedFor('MANAGE_PERMISSIONS');
  }

  canManageUsersResults(): boolean {
    return this.isAuthedFor('MANAGE_OTHER_USERS_RESULTS');
  }

  canManageTest(): boolean {
    return this.isAuthedFor('MANAGE_TESTS');
  }

  //eslint-disable-next-line
  getUISettingsValue(key) {
    const userSettings = get(this, 'settings', null);
    if (userSettings) {
      const uiSettings = find(userSettings, { key: 'UI' });
      return get(uiSettings, key);
    }
  }

  get readOnly(): boolean {
    return this.isAuthedFor('READ_ONLY') && !this.isAuthedFor('VIAVI_ADMIN');
  }

  setLDAPUser(name: string): void {
    this.ldapUser = name.includes('LDAP/');
  }

  get isLDAPUser(): boolean {
    return this.ldapUser;
  }

  //eslint-disable-next-line
  isAuthedFor(role) {
    const userData = JSON.parse(sessionStorage.getItem(this.STORAGE_KEY_USER_DATA));
    const roles = get(userData, 'USER_ROLES');
    if (roles) {
      return roles.includes(ROLES_MAP[role]);
    }
    return false;
  }

  //eslint-disable-next-line
  setShowNotificationPopups(isVisible) {
    this.changeUISettings(showNotificationPopups, !isVisible);
  }

  //eslint-disable-next-line
  get showRFactor() {
    return this.getUISettingsValue('value.voip_display_the_mos');
  }

  //eslint-disable-next-line
  getUserEmail() {
    return get(this, 'data.EMAIL', '');
  }

  //eslint-disable-next-line
  set showRFactor(isVisible) {
    this.changeUISettings('value.voip_display_the_mos', isVisible);
  }

  //eslint-disable-next-line
  get showAlerts() {
    return this.getUISettingsValue(showNotificationPopups);
  }

  //eslint-disable-next-line
  set showAlerts(isVisible) {
    this.changeUISettings(showNotificationPopups, isVisible);
  }

  //eslint-disable-next-line
  get showFirstStepWizard() {
    return this.getUISettingsValue('value.first_step_wizard_show');
  }

  //eslint-disable-next-line
  set showFirstStepWizard(isVisible) {
    this.changeUISettings('value.first_step_wizard_show', isVisible);
  }

  getUserAuth(): UserAuth {
    const user = JSON.parse(sessionStorage.getItem(this.STORAGE_KEY));
    const userData = JSON.parse(sessionStorage.getItem(this.STORAGE_KEY_USER_DATA));
    const userPref = JSON.parse(sessionStorage.getItem(this.STORAGE_KEY_USER_PREF));

    if (user) {
      this.settings = user.settings;
      this.token = user.token;
      this.data = userData;
      this.pref = userPref;
    }

    return this;
  }

  //eslint-disable-next-line
  getUserPref(): any {
    return JSON.parse(sessionStorage.getItem(this.STORAGE_KEY_USER_PREF));
  }

  //eslint-disable-next-line
  getUserIdleTimes(): any {
    const idleTime = this.isAuthedFor('MONITOR') ? IDLE_TIMES.MONITOR_USER : IDLE_TIMES.NORMAL_USER;
    return {
      idle: idleTime,
      timeout: IDLE_TIMES.DIALOG_TIMEOUT,
      ping: IDLE_TIMES.PING_TIMEOUT //not used
    };
  }

  //eslint-disable-next-line
  removeRoles(dataSource) {
    //eslint-disable-next-line
    remove<any>(dataSource, item => HIDE_ROLES[item.id]);
  }

  //eslint-disable-next-line
  addRoles(dataSource) {
    return concat(dataSource, ADD_ROLES);
  }

  //eslint-disable-next-line
  getRoleNameMapById(roleId) {
    return get(ROLE_NAME_MAP, `${roleId}`);
  }

  //eslint-disable-next-line
  getRoleFriendlyName(roleId) {
    return get(ROLE_NAME_MAP, `${roleId}.friendlyName`, roleId);
  }

  //eslint-disable-next-line
  getRoleDescriptionName(roleId) {
    return get(ROLE_NAME_MAP, `${roleId}.description`, '');
  }

  getUserName(): string {
    return get(this, 'data.USER_ID', '');
  }

  private _init(): void {
    for (let r = 0; r < 100; r++) {
      this.rFactor.push(r);
      this.mos.push(this.rFactorToMos(r));
    }
  }
}
